import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ScriptsContext } from '../context/ScriptsContext';
import { HITS_TYPES_BY_URL } from '../pages/DiscoverPage/const';
import { DiscoverContext } from '../context/DiscoverContext';

export const useEventListener = (eventName, handler, element = window, options?) => {
  // Create a ref that stores handler
  const savedHandler = useRef();

  // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(
    () => {
      // Make sure element supports addEventListener
      // On
      const isSupported = element && element.addEventListener;
      if (!isSupported) return;

      // Create event listener that calls handler function stored in ref
      // @ts-ignore
      const eventListener = (event) => savedHandler.current(event);

      // Add event listener
      element.addEventListener(eventName, eventListener, options);

      // Remove event listener on cleanup
      return () => {
        element.removeEventListener(eventName, eventListener, options);
      };
    },
    [eventName, element, options], // Re-run if eventName or element changes
  );
};

export const useMathJaxScriptLoader = () => {
  const { setMathJaxScriptLoaded, isLoadedMathJaxScript } = useContext(ScriptsContext);

  useEffect(() => {
    //check if script url exists and script not loaded yet
    if (!isLoadedMathJaxScript) {
      //mathjax config
      //@ts-ignore
      window.MathJax = {
        tex: {
          inlineMath: [['\\(', '\\)']],
          displayMath: [
            ['$$', '$$'],
            ['\\[', '\\]'],
            ['<formula>', '</formula>'],
          ],
        },
      };

      const script = document.createElement('script');

      script.src = `${process.env.REACT_APP_IMAGEKIT_URL}react-static-files/${process.env.REACT_APP_BUILD_TYPE}/tex-svg-full.js`;
      script.async = true;

      document.body.appendChild(script);

      script.onload = () => {
        setMathJaxScriptLoaded(true);
      };
    }
  }, [setMathJaxScriptLoaded, isLoadedMathJaxScript]);
};

export const useTeamsShareScriptLoader = () => {
  const { setTeamsShareScriptLoaded, isLoadedTeamsShareScript } = useContext(ScriptsContext);

  useEffect(() => {
    //check if script not loaded yet
    if (!isLoadedTeamsShareScript) {
      const script = document.createElement('script');

      script.src = 'https://teams.microsoft.com/share/launcher.js';
      script.async = true;

      document.body.appendChild(script);

      script.onload = () => {
        setTeamsShareScriptLoaded(true);
      };
    }
  }, [setTeamsShareScriptLoaded, isLoadedTeamsShareScript]);
};

export const useSERPNavigation = () => {
  const location = useLocation();
  const [type, setType] = useState('');

  useEffect(() => {
    const typeRegExp = new RegExp('[^/]+/?$', 'g');
    const match = location.pathname.match(typeRegExp);
    if (match && match[0]) {
      setType(match[0].replace(/\/$/, ''));
    }
  }, [location]);

  return HITS_TYPES_BY_URL[type];
};

export const useClearSearchState = (saveQuery = false) => {
  const { setSearchState } = useContext(DiscoverContext);
  const location = useLocation();
  const pathname = location?.pathname;

  useEffect(() => {
    return () => {
      setSearchState((prevState) => ({
        ...(saveQuery && { query: prevState?.query }),
      }));
    };
  }, [setSearchState, saveQuery, pathname]);
};

export type UseTablePaginationType = {
  page: number;
  rowsPerPage: number;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const useTablePagination = (
  defaultRowsPerPage = 50,
  defaultPage = 0,
): UseTablePaginationType => {
  const [page, setPage] = useState(defaultPage);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const handleChangePage = useCallback(
    (event: unknown, newPage: number) => {
      setPage(newPage);
    },
    [setPage],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [setRowsPerPage, setPage],
  );

  return {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  };
};

export const useIsIE = () => {
  const [isIE, setIsIE] = useState(false);

  useEffect(() => {
    //@ts-ignore
    const isIE = /*@cc_on!@*/ false || !!document?.documentMode;

    setIsIE(isIE);
  }, [setIsIE]);

  return isIE;
};

export const usePrevious = (value) => {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
};
